(() => {
  'use strict';

  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  ready(() => {
    const main = document.querySelector('main[data-template]');
    if (!main || !main.dataset.template?.includes('product')) return;

    const variantPicker = main.querySelector('variant-picker');
    const addButton = main.querySelector(
      'button[name="add"], add-to-cart-component button, .product-form__submit, [data-add-to-cart]'
    );

    const isPetHairRemover = window.location.pathname.startsWith('/products/reusable-pet-hair-remover-brush');
    const isBooties = window.location.pathname.replace(/\/$/, '') === '/products/christmas-elf-dog-booties';
    if (variantPicker && !isPetHairRemover && !main.querySelector('.vow-launch-purchase-help')) {
      const lowerPlacement = main.querySelector('.vow-gm__placement');
      const placementText = lowerPlacement?.textContent?.trim() || 'Review the product images for print placement';

      const helper = document.createElement('div');
      helper.className = isBooties ? 'vow-launch-purchase-help vow-launch-purchase-help--booties' : 'vow-launch-purchase-help';
      helper.setAttribute('aria-label', 'Product purchase help');
      helper.innerHTML = `
        <div class="vow-launch-purchase-help__placement"><span aria-hidden="true">🐾</span><span></span></div>
        <nav class="vow-launch-purchase-help__links" aria-label="Sizing, shipping, and returns">
          <a href="/pages/size-guide">Size Guide</a>
          <a href="/pages/shipping-delivery">Shipping</a>
          <a href="/pages/returns-refunds">Returns</a>
        </nav>
        <p class="vow-launch-order-note">Printed to order. Confirm your size, color, and artwork placement before checkout.</p>
      `;
      const textTarget = helper.querySelector('.vow-launch-purchase-help__placement span:last-child');
      if (textTarget) textTarget.textContent = placementText;

      // Place the supplied booties size guide directly beneath Size 2–5,
      // outside (and before) the beige purchase-help panel.
      let sizeChartLink = null;
      if (isBooties) {
        const sizeChartUrl = 'https://cdn.shopify.com/s/files/1/1026/9592/8177/files/vowhound-christmas-elf-dog-booties-size-guide-under-size-options.jpg?v=1793070484';
        const chartLink = document.createElement('a');
        chartLink.className = 'vow-launch-size-chart';
        chartLink.href = sizeChartUrl;
        chartLink.target = '_blank';
        chartLink.rel = 'noopener';
        chartLink.setAttribute('aria-label', 'View larger Christmas Elf Dog Booties size guide');

        const chartImage = document.createElement('img');
        chartImage.className = 'vow-launch-size-chart__image';
        chartImage.src = sizeChartUrl;
        chartImage.alt = 'Christmas Elf Dog Booties size chart for sizes 2, 3, 4 and 5, with measurements in inches and centimeters';
        chartImage.loading = 'lazy';
        chartImage.decoding = 'async';
        chartLink.appendChild(chartImage);

        sizeChartLink = chartLink;
        const sizeLink = helper.querySelector('.vow-launch-purchase-help__links a[href="/pages/size-guide"]');
        if (sizeLink) {
          sizeLink.href = '#vowhound-booties-size-guide';
          sizeLink.textContent = 'Dog booties size guide';
        }

        const orderNote = helper.querySelector('.vow-launch-order-note');
        if (orderNote) orderNote.textContent = "Measure your dog's paws and check the shipping estimate before checkout.";
      }

      variantPicker.insertAdjacentElement('afterend', helper);
      if (sizeChartLink) variantPicker.insertAdjacentElement('afterend', sizeChartLink);
    }

    if (!addButton || document.querySelector('.vow-launch-mobile-cta')) return;

    const mobileCta = document.createElement('button');
    mobileCta.type = 'button';
    mobileCta.className = 'vow-launch-mobile-cta';
    mobileCta.dataset.visible = 'false';
    mobileCta.setAttribute('aria-label', 'Go to product purchase controls');
    document.body.appendChild(mobileCta);

    const syncButton = () => {
      const text = addButton.textContent?.trim() || 'Choose options';
      mobileCta.textContent = addButton.disabled ? text : `Choose options — ${text}`;
      mobileCta.disabled = Boolean(addButton.disabled);
    };
    syncButton();

    const observer = new MutationObserver(syncButton);
    observer.observe(addButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    mobileCta.addEventListener('click', () => {
      const target = variantPicker || addButton;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const firstControl = variantPicker?.querySelector('input:not(:disabled), select:not(:disabled), button:not(:disabled)');
        (firstControl || addButton).focus({ preventScroll: true });
      }, 450);
    });

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          const shouldShow = window.matchMedia('(max-width: 749px)').matches && !entry.isIntersecting;
          mobileCta.dataset.visible = shouldShow ? 'true' : 'false';
        },
        { threshold: 0.15 }
      );
      visibilityObserver.observe(addButton);
    }

    const mediaQuery = window.matchMedia('(max-width: 749px)');
    const hideOnDesktop = () => {
      if (!mediaQuery.matches) mobileCta.dataset.visible = 'false';
    };
    mediaQuery.addEventListener?.('change', hideOnDesktop);
  });
})();

/* VOWHOUND — show real product color options with animated card previews. */
(() => {
  'use strict';

  const productCache = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let staggerSeed = 0;

  const getProductEndpoint = (href) => {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/[^/?#]+/);
      return match ? `${match[0]}.js` : null;
    } catch (_error) {
      return null;
    }
  };

  const sizeImage = (src) => {
    try {
      const url = new URL(src, window.location.origin);
      if (url.hostname.includes('cdn.shopify.com')) url.searchParams.set('width', '900');
      return url.href;
    } catch (_error) {
      return src;
    }
  };

  const readOptionName = (option) => {
    if (typeof option === 'string') return option;
    return option?.name || '';
  };

  const readOptionValues = (option) => {
    if (Array.isArray(option?.values)) return option.values;
    return [];
  };

  const getVariantImage = (variant) => {
    const image = variant?.featured_image || variant?.featured_media?.preview_image;
    if (typeof image === 'string') return image;
    return image?.src || image?.url || null;
  };

  const loadColorData = async (href) => {
    const endpoint = getProductEndpoint(href);
    if (!endpoint) return null;
    if (productCache.has(endpoint)) return productCache.get(endpoint);

    const request = fetch(endpoint, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return null;

        const options = Array.isArray(product.options) ? product.options : [];
        const colorIndex = options.findIndex((option) => /colou?r/i.test(readOptionName(option)));
        if (colorIndex < 0) return null;

        const colorMap = new Map();
        for (const variant of product.variants || []) {
          const color = variant?.options?.[colorIndex] || variant?.[`option${colorIndex + 1}`];
          const src = getVariantImage(variant);
          if (!color || !src || colorMap.has(color)) continue;
          colorMap.set(color, { color, src: sizeImage(src) });
        }

        const declaredColors = readOptionValues(options[colorIndex]).length;
        const totalColors = Math.max(declaredColors, colorMap.size);
        const images = Array.from(colorMap.values()).slice(0, 4);

        if (images.length < 2) return null;
        return { images, totalColors };
      })
      .catch(() => null);

    productCache.set(endpoint, request);
    return request;
  };

  const findAnchor = (host) => {
    if (host.matches('a[href*="/products/"]')) return host;
    if (host.classList.contains('vow-gm__product-media')) return host.closest('a[href*="/products/"]');
    return host.querySelector('a[href*="/products/"]') || host.closest('a[href*="/products/"]');
  };

  const stopCycle = (state) => {
    window.clearTimeout(state.timeout);
    window.clearInterval(state.interval);
    state.timeout = null;
    state.interval = null;
  };

  const startCycle = (state, delay) => {
    if (reduceMotion.matches || !state.visible || state.images.length < 2) return;
    stopCycle(state);

    const advance = () => {
      const previous = state.index;
      state.index = (state.index + 1) % state.images.length;
      state.images[state.index].classList.add('is-active');
      window.setTimeout(() => state.images[previous]?.classList.remove('is-active'), 70);
    };

    const firstDelay = Math.min(delay, 900 + (state.seed % 4) * 260);
    state.timeout = window.setTimeout(() => {
      advance();
      state.interval = window.setInterval(advance, delay);
    }, firstDelay);
  };

  const buildCycle = async (host) => {
    if (host.dataset.vowColorCycleRequested === 'true') return;
    host.dataset.vowColorCycleRequested = 'true';

    const anchor = findAnchor(host);
    const data = await loadColorData(anchor?.href);
    if (!data || !host.isConnected || host.querySelector(':scope > .vow-color-cycle')) return;

    host.classList.add('vow-color-cycle-host');

    const overlay = document.createElement('div');
    overlay.className = 'vow-color-cycle';
    overlay.setAttribute('aria-hidden', 'true');

    const images = data.images.map((item, index) => {
      const image = document.createElement('img');
      image.className = `vow-color-cycle__image${index === 0 ? ' is-active' : ''}`;
      image.src = item.src;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      overlay.appendChild(image);
      return image;
    });

    const badge = document.createElement('span');
    badge.className = 'vow-color-cycle__badge';
    badge.textContent = `${data.totalColors} ${data.totalColors === 1 ? 'color' : 'colors'}`;
    overlay.appendChild(badge);
    host.appendChild(overlay);

    const state = {
      host,
      images,
      index: 0,
      visible: true,
      seed: staggerSeed++,
      timeout: null,
      interval: null,
    };

    host.addEventListener('pointerenter', () => startCycle(state, 1250));
    host.addEventListener('pointerleave', () => startCycle(state, 2600));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          state.visible = entry.isIntersecting;
          if (state.visible) startCycle(state, 2600);
          else stopCycle(state);
        },
        { rootMargin: '120px 0px', threshold: 0.08 }
      );
      observer.observe(host);
    } else {
      startCycle(state, 2600);
    }
  };

  const scan = (root = document) => {
    const selectors = [
      'product-card .card-gallery',
      '.product-card .card-gallery',
      '.vh-product-card__media',
      '.vow-gm__product-media',
    ];

    const hosts = new Set();
    for (const selector of selectors) {
      root.querySelectorAll?.(selector).forEach((node) => hosts.add(node));
      if (root.matches?.(selector)) hosts.add(root);
    }

    hosts.forEach((host) => buildCycle(host));
  };

  const ready = () => {
    scan(document);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) scan(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
